import { ChangeEvent, memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

import { TwoFactorAuthentication } from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';

interface Props {
  errorMessage?: string;
  isLoading?: boolean;
  onTwoFactorAuthenticationDisablerChange: (verification: TwoFactorAuthentication) => void;
  twoFactorAuthenticationDisabler: TwoFactorAuthentication;
  twoFactorAuthenticationDisablers: Array<TwoFactorAuthentication>;
}

const VerificationProvider = (props: Props): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const {
    errorMessage = undefined,
    isLoading,
    onTwoFactorAuthenticationDisablerChange,
    twoFactorAuthenticationDisabler,
    twoFactorAuthenticationDisablers,
  } = props;

  const handleVerificationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMethod = twoFactorAuthenticationDisablers.find(
        (method) => method.id === e.target.value
      );

      onTwoFactorAuthenticationDisablerChange(newMethod);
    },
    [onTwoFactorAuthenticationDisablerChange, twoFactorAuthenticationDisablers]
  );

  return (
    <>
      <Box mt={2} mb={3}>
        <Typography gutterBottom>
          <FormattedMessage defaultMessage="To disable your old SMS number, please verify your identity by sending a verification code to one of the authentication providers below." />
        </Typography>
      </Box>
      <Box my={3}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="authentication-provider-label">
            <FormattedMessage defaultMessage="Authentication Provider" />
          </InputLabel>

          <Select
            variant="standard"
            error={Boolean(errorMessage)}
            value={twoFactorAuthenticationDisabler?.id ?? twoFactorAuthenticationDisablers[0].id}
            labelId="authentication-provider-label"
            id="authentication-provider"
            defaultValue={twoFactorAuthenticationDisabler?.id}
            onChange={handleVerificationChange}
            label={formatMessage({
              defaultMessage: 'Authentication Provider',
            })}
            autoFocus
            disabled={isLoading}
          >
            {twoFactorAuthenticationDisablers.map((method) => (
              <MenuItem key={method.id} value={method.id}>
                {method.displayName}
              </MenuItem>
            ))}
          </Select>

          {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
      </Box>
    </>
  );
};

export default memo(VerificationProvider);
